/**
 * blankRenderVerifier — post-render canary for GPUs that create a WebGL
 * context and pass the synthetic probes but composite blank output on real
 * frames (CS-2844: old ARM64 Surface tablets).
 *
 * Created per DeckOverlay attach and wired as deck.gl's `onAfterRender`.
 * After each frame it projects a handful of layer datapoints to screen space
 * and reads back small pixel patches around them. Markers in view plus
 * several spaced-out fully-transparent frames → flips the session-wide
 * WebGL fatal flag, so every `useWebGLSupport()` consumer cascades to DOM
 * markers automatically.
 *
 * The first frame with visible content disarms the verifier for the lifetime
 * of the overlay — healthy devices pay a few small readbacks once.
 */
import { reportWebGLBlankRender } from './useWebGLSupport'

const REQUIRED_BLANK_FRAMES = 3
// Texture upload can lag the first frames after data arrives; blank frames
// inside the warmup window are ignored rather than counted.
const WARMUP_MS = 1500
// During zoom/pan deck renders continuously, and a layer swap (e.g. an icon
// atlas mid-upload) yields a short burst of legitimately blank frames. Spacing
// strikes out keeps a sub-second burst from reaching the threshold, while a
// persistently blank device still trips in ~1s.
const STRIKE_SPACING_MS = 400
const MAX_SAMPLES = 5
// Skip datapoints near the viewport edge — their pixels may be clipped.
const EDGE_MARGIN_PX = 24
// Icons anchor bottom-center, so the patch is biased upward to land inside
// the marker body regardless of exact icon height.
const PATCH_CSS_PX = 16

function patchHasAlpha(gl, centerX, centerY, size) {
  const x = Math.max(0, Math.min(gl.drawingBufferWidth - size, Math.round(centerX - size / 2)))
  // readPixels origin is bottom-left; projection origin is top-left.
  const y = Math.max(0, Math.min(gl.drawingBufferHeight - size, Math.round(gl.drawingBufferHeight - centerY - size / 2)))
  const pixels = new Uint8Array(size * size * 4)
  gl.readPixels(x, y, size, size, gl.RGBA, gl.UNSIGNED_BYTE, pixels)
  for (let i = 3; i < pixels.length; i += 4) {
    if (pixels[i] > 0) return true
  }
  return false
}

export function createBlankRenderVerifier({ componentName, getDeck }) {
  let done = false
  let blankFrames = 0
  let lastStrikeAt = 0
  let redrawPending = false
  const armedAt = Date.now()

  // A static map stops producing frames once tiles settle, which could starve
  // the verifier of post-warmup samples. After every blank frame, request
  // another one so a verdict is always reached. getDeck() returns undefined
  // after the overlay unmounts, making a late timer a no-op.
  const scheduleRedraw = () => {
    if (redrawPending) return
    redrawPending = true
    setTimeout(() => {
      redrawPending = false
      if (!done) getDeck()?.redraw?.('blank-render-verify')
    }, 500)
  }

  return function verifyAfterRender({ gl }) {
    if (done || !gl) return
    // A thrown readPixels/project (e.g. mid-frame context loss) must not break
    // deck's render loop — treat the frame as "no verdict" and move on. A truly
    // dead context surfaces through the luma.gl window listeners instead.
    try {
      const deck = getDeck()
      if (!deck) return
      const viewport = deck.getViewports?.()[0]
      if (!viewport?.width || !viewport?.height) return

      const layers = (deck.props?.layers || []).flat(Infinity).filter(Boolean)
      const dataLayer = layers.find(
        layer => Array.isArray(layer.props?.data) && layer.props.data.length > 0 && typeof layer.props?.getPosition === 'function'
      )
      if (!dataLayer) return

      const { data, getPosition } = dataLayer.props
      const stride = Math.max(1, Math.floor(data.length / 25))
      const samples = []
      for (let i = 0; i < data.length && samples.length < MAX_SAMPLES; i += stride) {
        const position = getPosition(data[i])
        if (Array.isArray(position)) {
          const [x, y] = viewport.project([position[0], position[1]])
          const inView =
            x >= EDGE_MARGIN_PX && y >= EDGE_MARGIN_PX && x <= viewport.width - EDGE_MARGIN_PX && y <= viewport.height - EDGE_MARGIN_PX
          if (inView) samples.push([x, y])
        }
      }
      // Nothing expected on screen — not a verdict either way.
      if (samples.length === 0) return

      const pixelRatio = gl.drawingBufferWidth / viewport.width
      const patch = Math.max(8, Math.round(PATCH_CSS_PX * pixelRatio))
      const hasContent = samples.some(([x, y]) => patchHasAlpha(gl, x * pixelRatio, (y - PATCH_CSS_PX / 2) * pixelRatio, patch))

      if (hasContent) {
        done = true
        return
      }
      const now = Date.now()
      if (now - armedAt < WARMUP_MS) {
        scheduleRedraw()
        return
      }
      if (now - lastStrikeAt >= STRIKE_SPACING_MS) {
        lastStrikeAt = now
        blankFrames += 1
        if (blankFrames >= REQUIRED_BLANK_FRAMES) {
          done = true
          reportWebGLBlankRender(componentName)
          return
        }
      }
      scheduleRedraw()
    } catch {
      // no-op: skip this frame
    }
  }
}
